// Полетата на „Справка отчети за дата" — отчетът от фискалната памет.
// Разгъват се ВЕДНАГА под избраната опция (не в дъното на модала):
// „Период на отчета" като тогъл Предходен месец / Този месец / Период
// (оранжевият pill на темата) с точните дати под избора; при „Период" — два
// наши календара От/До без таван (дългият подробен отчет пита в
// OperationsModal); същият тогъл за вида Съкратен / Подробен. Границите на
// пресетите и праговете са в fmReportPeriod.ts. Само Tailwind; цветовете са
// семантичните токени + акцентът на темата (brand; antd календарът го
// получава като hex от палитрата), за да работи във всяка тема.
import { ConfigProvider, DatePicker } from "antd";
import dayjs, { type Dayjs } from "dayjs";
import { useTranslation } from "react-i18next";
import type { FmReportDetail } from "../../../../../api/operations";
import { useThemePalette } from "../../../../../store/hooks";
import {
  FM_REPORT_LONG_PERIOD_DAYS,
  MONTHLY_REPORT_DEADLINE_DAY,
  fmPresetRange,
  type FmQuickPreset,
  type FmReportPreset,
} from "./fmReportPeriod";

const DATE_FORMAT = "DD.MM.YYYY";


interface ToggleOption<T extends string> {
  value: T;
  label: string;
}

interface BrandToggleProps<T extends string> {
  value: T;
  options: ToggleOption<T>[];
  onChange: (value: T) => void;
  disabled: boolean;
}

// Тогълът от мокъпите: светла писта, активният сегмент е плътен бранд
// pill с бял текст. Генеричен по стойността, за да служи и на периода
// (3 опции), и на вида (2 опции).
const BrandToggle = <T extends string>({
  value,
  options,
  onChange,
  disabled,
}: BrandToggleProps<T>) => (
  <div className="flex w-full gap-[3px] rounded-[12px] bg-[var(--color-shade-1)] p-[3px]">
    {options.map((option) => {
      const isActive = option.value === value;
      return (
        <button
          key={option.value}
          type="button"
          disabled={disabled}
          onClick={() => onChange(option.value)}
          className={
            "flex-1 rounded-[9px] px-2 py-[9px] text-[13.5px] font-semibold leading-[1.2] transition-colors duration-150 " +
            (isActive
              ? "bg-brand text-white "
              : "bg-transparent text-[var(--color-body)] hover:bg-[var(--color-surface)] ") +
            (disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer")
          }
        >
          {option.label}
        </button>
      );
    })}
  </div>
);

const FieldLabel = ({ children }: { children: string }) => (
  <div className="text-[10.5px] font-bold uppercase tracking-[1.2px] text-[var(--color-muted)]">
    {children}
  </div>
);

const Caption = ({ children }: { children: string }) => (
  <p className="m-0 text-[12px] leading-[1.4] text-[var(--color-muted)]">
    {children}
  </p>
);

interface FmReportFieldsProps {
  detail: FmReportDetail;
  onDetailChange: (detail: FmReportDetail) => void;
  preset: FmReportPreset;
  onPresetChange: (preset: FmReportPreset) => void;
  from: Dayjs | null;
  onFromChange: (value: Dayjs | null) => void;
  to: Dayjs | null;
  onToChange: (value: Dayjs | null) => void;
  disabled: boolean;
}

export const FmReportFields = ({
  detail,
  onDetailChange,
  preset,
  onPresetChange,
  from,
  onFromChange,
  to,
  onToChange,
  disabled,
}: FmReportFieldsProps) => {
  const { t } = useTranslation();
  const themePalette = useThemePalette();
  // Фискалната памет няма нищо след днес — бъдещите дни са изключени
  // направо в календара, не чак при изпращане.
  const disableFutureDates = (value: Dayjs) =>
    value.isAfter(dayjs().endOf("day"));

  // Подсказката под бързия избор показва точните дати, които ще се отпечатат.
  const presetCaption = (quickPreset: FmQuickPreset) => {
    const range = fmPresetRange(quickPreset);
    return t(
      quickPreset === "lastMonth"
        ? "operations.fmPresetLastMonthHint"
        : "operations.fmPresetThisMonthHint",
      {
        from: range.start.format(DATE_FORMAT),
        to: range.end.format(DATE_FORMAT),
        deadlineDay: MONTHLY_REPORT_DEADLINE_DAY,
      },
    );
  };

  return (
    <div className="flex flex-col gap-3 px-1 pb-1">
      <div className="flex flex-col gap-1.5">
        <FieldLabel>{t("operations.fmReportPeriod")}</FieldLabel>
        <BrandToggle<FmReportPreset>
          value={preset}
          disabled={disabled}
          onChange={onPresetChange}
          options={[
            { value: "lastMonth", label: t("operations.fmPresetLastMonth") },
            { value: "thisMonth", label: t("operations.fmPresetThisMonth") },
            { value: "period", label: t("operations.fmPresetPeriod") },
          ]}
        />
        {preset !== "period" && <Caption>{presetCaption(preset)}</Caption>}
        {preset === "period" && (
          <div className="flex flex-col gap-1.5 pt-1">
            <ConfigProvider theme={{ token: { colorPrimary: themePalette.accent } }}>
              <div className="grid grid-cols-2 gap-2.5">
                <div className="flex flex-col gap-1.5">
                  <FieldLabel>{t("operations.fmPeriodFrom")}</FieldLabel>
                  <DatePicker
                    value={from}
                    onChange={onFromChange}
                    format={DATE_FORMAT}
                    allowClear={false}
                    disabled={disabled}
                    disabledDate={disableFutureDates}
                    className="w-full !h-[42px] !rounded-[10px]"
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <FieldLabel>{t("operations.fmPeriodTo")}</FieldLabel>
                  <DatePicker
                    value={to}
                    onChange={onToChange}
                    format={DATE_FORMAT}
                    allowClear={false}
                    disabled={disabled}
                    disabledDate={disableFutureDates}
                    className="w-full !h-[42px] !rounded-[10px]"
                  />
                </div>
              </div>
            </ConfigProvider>
            <Caption>
              {t("operations.fmPeriodLongSpanHint", {
                days: FM_REPORT_LONG_PERIOD_DAYS,
              })}
            </Caption>
          </div>
        )}
      </div>

      <div className="flex flex-col gap-1.5">
        <FieldLabel>{t("operations.fmReportDetail")}</FieldLabel>
        <BrandToggle<FmReportDetail>
          value={detail}
          disabled={disabled}
          onChange={onDetailChange}
          options={[
            { value: "short", label: t("operations.fmReportShort") },
            { value: "full", label: t("operations.fmReportFull") },
          ]}
        />
        <Caption>
          {detail === "short"
            ? t("operations.fmReportShortHint")
            : t("operations.fmReportFullHint")}
        </Caption>
      </div>
    </div>
  );
};
